<script setup>
import { computed, defineAsyncComponent, getCurrentInstance, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { shortenString } from '@/utils/strings.js'

const BaseButton = defineAsyncComponent(() =>
  import(/* webpackChunkName: "components/base/buttons/BaseButton.vue" */ '@/components/base/buttons/BaseButton.vue')
)
const BackBtn = defineAsyncComponent(() =>
  import(/* webpackChunkName: "components/base/buttons/BackBtn.vue" */ '@/components/base/buttons/BackBtn.vue')
)
const Copy = defineAsyncComponent(() =>
  import(/* webpackChunkName: "components/base/clipboardCopy/copy.vue" */ '@/components/base/clipboardCopy/copy.vue')
)
const BaseLoader = defineAsyncComponent(() =>
  import(/* webpackChunkName: "components/base/BaseLoader.vue" */ '@/components/base/BaseLoader.vue')
)
const CurrencyAmount = defineAsyncComponent(() =>
  import(/* webpackChunkName: "components/base/CurrencyAmount" */ '@/components/base/CurrencyAmount.vue')
)

const instance = getCurrentInstance()
const $http = instance.proxy.$http
const text = instance.proxy.text
const route = useRoute()
const router = useRouter()
const vuexStore = useStore()

const transaction = ref(null)
const loading = ref(false)
const errorMessage = ref('')
const pollingIntervalId = ref(null)
const pollingStopTimeoutId = ref(null)
const pollingIntervalMs = 5000
const pollingMaxDurationMs = 120000

const authToken = computed(() => vuexStore.state.authToken)
const exchange = computed(() => transaction.value?.exchange || null)
const isFinalStatus = computed(() =>
  ['complete', 'failed'].includes(transaction.value?.status)
)

const statusText = computed(() => {
  if (!transaction.value?.status) {
    return ''
  }
  return text(`transactions.list.status.${transaction.value.status}`)
})

function exchangeSide(side) {
  const item = exchange.value?.[side]
  if (!item || item.amount === null || !item.currency_code) {
    return null
  }
  return item
}

function clearPolling() {
  if (pollingIntervalId.value) {
    window.clearInterval(pollingIntervalId.value)
    pollingIntervalId.value = null
  }
  if (pollingStopTimeoutId.value) {
    window.clearTimeout(pollingStopTimeoutId.value)
    pollingStopTimeoutId.value = null
  }
}

function stopAutoPolling() {
  if (pollingIntervalId.value) {
    window.clearInterval(pollingIntervalId.value)
    pollingIntervalId.value = null
  }
  if (pollingStopTimeoutId.value) {
    window.clearTimeout(pollingStopTimeoutId.value)
    pollingStopTimeoutId.value = null
  }
}

function startPolling() {
  clearPolling()

  if (isFinalStatus.value) {
    return
  }

  pollingIntervalId.value = window.setInterval(() => {
    fetchTransaction({ silent: true })
  }, pollingIntervalMs)

  pollingStopTimeoutId.value = window.setTimeout(() => {
    stopAutoPolling()
  }, pollingMaxDurationMs)
}

async function fetchTransaction(options = {}) {
  const silent = Boolean(options.silent)
  if (!silent) {
    loading.value = true
  }
  errorMessage.value = ''
  try {
    const { data } = await $http.get(
      `/v2/wallet/transaction/${route.params.id}`,
      {
        headers: {
          Authorization: 'Token ' + authToken.value,
        },
        validateStatus: () => true,
      }
    )

    if (data?.result?.type === 'exchange') {
      transaction.value = data.result
      if (isFinalStatus.value) {
        stopAutoPolling()
      }
    } else {
      errorMessage.value = data?.message || text('exchange.status_modal.try_again_later')
    }
  } finally {
    if (!silent) {
      loading.value = false
    }
  }
}

function goHome() {
  router.push({ name: 'Home' })
}

onMounted(async () => {
  await fetchTransaction()
  startPolling()
})

onUnmounted(() => {
  clearPolling()
})
</script>

<template>
  <div class="exchange exchange-transaction">
    <div class="exchange_head">
      <BackBtn @click="$router.back()" />
      <h2>{{ text('exchange.transaction.title') }}</h2>
    </div>

    <BaseLoader v-if="loading && !transaction" />
    <div v-else-if="errorMessage" class="error">{{ errorMessage }}</div>
    <template v-else-if="transaction">
      <div class="exchange-status">
        <div class="title">
          {{ statusText }}
        </div>
        <div class="exchange-info">
          <div class="row">
            <div class="label">{{ text('transactions.list.details.transaction.id') }}</div>
            <div class="value transaction-id">
              {{ shortenString(transaction.id) }}
              <Copy :value="transaction.id" />
            </div>
          </div>
          <div class="row">
            <div class="label">{{ text('transactions.list.details.transaction.type') }}</div>
            <div class="value">{{ text('transactions.list.exchange') }}</div>
          </div>
          <div class="row">
            <div class="label">{{ text('transactions.list.details.transaction.status') }}</div>
            <div class="value">{{ statusText }}</div>
          </div>
          <div class="row">
            <div class="label">{{ text('transactions.list.details.transaction.date') }}</div>
            <div class="value">{{ formatDateTime(transaction.created_at) }}</div>
          </div>
          <div class="row">
            <div class="label">{{ text('exchange.from') }}</div>
            <div class="value from">
              <CurrencyAmount
                v-if="exchangeSide('from')"
                :amount="exchangeSide('from').amount"
                :currency-code="exchangeSide('from').currency_code"
                prefix="-"
              />
              <template v-else>-</template>
            </div>
          </div>
          <div class="row">
            <div class="label">{{ text('exchange.to') }}</div>
            <div class="value to">
              <CurrencyAmount
                v-if="exchangeSide('to')"
                :amount="exchangeSide('to').amount"
                :currency-code="exchangeSide('to').currency_code"
                prefix="+"
              />
              <template v-else>-</template>
            </div>
          </div>
        </div>

        <div class="actions">
          <BaseButton v-if="!isFinalStatus" grayscale-accent @click="fetchTransaction">
            <template #label>
              {{ text('exchange.transaction.refresh_status') }}
            </template>
          </BaseButton>
          <BaseButton grayscale @click="goHome">
            <template #label>
              {{ text('common.actions.close') }}
            </template>
          </BaseButton>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.exchange-transaction {
  .exchange_head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
  }

  .exchange-status {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .exchange-info {
    display: flex;
    flex-direction: column;
    gap: 12px;
    border-radius: 8px;
    background: var(--element-bg);
    padding: 16px;
  }

  .row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    color: var(--text-color-primary);
    font-family: "Rubik", sans-serif;
    font-size: 14px;
    line-height: 135%;
  }

  .label {
    color: var(--text-color-secondary);
  }

  .value {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    min-width: 0;
    text-align: right;
    word-break: break-word;
  }

  .from {
    color: #F34B47;
    font-weight: 600;
  }

  .to {
    color: #00BF8E;
    font-weight: 600;
  }

  .title {
    color: var(--text-color-primary);
    font-family: "Rubik", sans-serif;
    font-size: 20px;
    font-weight: 600;
    text-align: center;
  }

  .actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}
</style>
